import { useEffect, useState } from "react";
import { X, CheckCircle } from "lucide-react";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import { bugsnagClient } from "bugsnag";
import { ANALYTICS_EVENTS, trackEvent } from "utils/analytics";
import api from "utils/api";
import fetchWithRetry from "utils/fetchWithRetry";
import type { FileCategory, UploadKind } from "../shared/types";

interface MultipartContext {
  key: string;
  uploadId: string;
  partSize: number;
  partCount: number;
  parts: { PartNumber: number; ETag: string | null }[];
  nextPartNumber: number;
  file: File;
  category: FileCategory;
}

interface SmartSurveyorUploadWizardProps {
  projectId: number;
  uploadKind: UploadKind;
  selectedFiles: Record<FileCategory, File[]>;
  onUploadComplete?: (result: any) => void;
  onClose: () => void;
  onRemoveFile?: (category: FileCategory, file: File) => void;
}

// TODO: resume-on-error path only re-completes the one resumed file; sibling files in the same batch whose parts already uploaded are left as orphan S3 parts with no DB row. Fix later.
export const SmartSurveyorUploadWizard = ({
  projectId,
  uploadKind,
  selectedFiles,
  onUploadComplete,
  onClose,
  onRemoveFile,
}: SmartSurveyorUploadWizardProps) => {
  const { t } = useTranslation();

  const [uploading, setUploading] = useState(false);
  const [uploadStatus, setUploadStatus] = useState<
    "idle" | "uploading" | "processing" | "success" | "error"
  >("idle");
  const [uploadStage, setUploadStage] = useState(""); // preparing, uploading, finalizing
  const [mpCtxMap, setMpCtxMap] = useState<Record<string, MultipartContext>>(
    {},
  );
  const [uploadProgress, setUploadProgress] = useState<Record<string, number>>(
    {},
  );
  const [, setUploadResult] = useState<any>(null);

  const filesArray = Object.entries(selectedFiles).flatMap(
    ([category, files]) =>
      files.map((file) => ({ file, category: category as FileCategory })),
  );

  useEffect(() => {
    if (filesArray.length === 0 && uploadStatus === "idle") {
      onClose();
    }
  }, [filesArray.length, uploadStatus, onClose]);

  const startUpload = async () => {
    if (!filesArray.length) return;

    const uploadStartedAt = Date.now();
    const totalSizeBytes = filesArray.reduce(
      (sizeSum, { file }) => sizeSum + file.size,
      0,
    );
    const totalSizeMb = Math.round((totalSizeBytes / (1024 * 1024)) * 10) / 10;
    trackEvent(ANALYTICS_EVENTS.SMART_SURVEYOR_UPLOAD_STARTED, {
      uploadKind,
      fileCount: filesArray.length,
      totalSizeMb,
    });
    setUploading(true);
    setUploadStatus("uploading");
    setUploadStage(t("smartSurveyor.uploading.stage.preparing"));

    const partSize = 10 * 1024 * 1024; // 10MB

    try {
      const mpContexts: Record<string, MultipartContext> = {};
      for (const { file, category } of filesArray) {
        const { key, uploadId } = await api.initiateQuantitySurveyorMultipart(
          projectId.toString(),
          file.name,
          file.type || "application/octet-stream",
        );
        const partCount = Math.ceil(file.size / partSize);

        mpContexts[key] = {
          key,
          uploadId,
          partSize,
          partCount,
          parts: [],
          nextPartNumber: 1,
          file,
          category,
        };

        setUploadProgress((prev) => ({ ...prev, [key]: 0 }));
      }
      setMpCtxMap(mpContexts);

      for (const key of Object.keys(mpContexts)) {
        const ctx = mpContexts[key];
        setUploadStage(
          `${t("smartSurveyor.uploading.stage.uploading")}: ${ctx.file.name}`,
        );

        for (
          let partNumber = ctx.nextPartNumber;
          partNumber <= ctx.partCount;
          partNumber++
        ) {
          const start = (partNumber - 1) * ctx.partSize;
          const end = Math.min(partNumber * ctx.partSize, ctx.file.size);
          const blobPart = ctx.file.slice(start, end);

          const { uploadUrl } = await api.presignMultipartPart(
            ctx.key,
            ctx.uploadId,
            partNumber,
          );
          const resp = await fetchWithRetry(uploadUrl, {
            method: "PUT",
            body: blobPart,
          });
          if (!resp.ok)
            throw new Error(
              `${t("smartSurveyor.uploading.error.partFailed")} ${partNumber} - ${ctx.file.name}`,
            );

          const eTag = resp.headers.get("ETag") || resp.headers.get("etag");
          ctx.parts.push({
            PartNumber: partNumber,
            ETag: eTag ? eTag.replaceAll('"', "") : null,
          });
          ctx.nextPartNumber = partNumber + 1;

          setUploadProgress((prev) => ({
            ...prev,
            [ctx.key]: Math.floor((partNumber / ctx.partCount) * 100),
          }));
          setMpCtxMap({ ...mpContexts });
        }
      }

      setUploadStage(t("smartSurveyor.uploading.stage.finalizing"));
      setUploadStatus("processing");

      const filesToComplete = Object.values(mpContexts).map((ctx) => ({
        type: ctx.category,
        key: ctx.key,
        uploadId: ctx.uploadId,
        parts: ctx.parts,
      }));

      const results = await api.completeQuantitySurveyorMultipart(
        projectId,
        filesToComplete,
      );

      setUploadResult(results);
      setUploadProgress({});
      setUploadStatus("success");
      setUploadStage(t("smartSurveyor.uploading.stage.completed"));
      setMpCtxMap({});
      trackEvent(ANALYTICS_EVENTS.SMART_SURVEYOR_UPLOAD_COMPLETED, {
        uploadKind,
        fileCount: filesArray.length,
        totalSizeMb,
        durationSeconds: Math.round((Date.now() - uploadStartedAt) / 1000),
      });

      if (onUploadComplete) onUploadComplete(results);

      // setTimeout(() => onClose(), 2000);
    } catch (error: any) {
      console.error("SmartSurveyor upload error:", error);
      trackEvent(ANALYTICS_EVENTS.SMART_SURVEYOR_UPLOAD_FAILED, {
        uploadKind,
        fileCount: filesArray.length,
        error: error instanceof Error ? error.message : String(error),
      });
      toast.error(
        error.message || t("smartSurveyor.uploading.error.uploadFailed"),
      );
      setUploadStatus("error");
      setUploadStage(t("smartSurveyor.uploading.error.uploadFailed"));
      bugsnagClient.notify(error);
    }
  };

  const resumeUpload = async (ctx: MultipartContext) => {
    try {
      trackEvent(ANALYTICS_EVENTS.SMART_SURVEYOR_UPLOAD_RESUMED, {
        uploadKind,
        fileName: ctx.file.name,
      });
      setUploadStatus("uploading");
      setUploadStage(
        `${t("smartSurveyor.uploading.stage.resuming")}: ${ctx.file.name}`,
      );

      for (
        let partNumber = ctx.nextPartNumber;
        partNumber <= ctx.partCount;
        partNumber++
      ) {
        const start = (partNumber - 1) * ctx.partSize;
        const end = Math.min(partNumber * ctx.partSize, ctx.file.size);
        const blobPart = ctx.file.slice(start, end);

        const { uploadUrl } = await api.presignMultipartPart(
          ctx.key,
          ctx.uploadId,
          partNumber,
        );
        const resp = await fetchWithRetry(uploadUrl, {
          method: "PUT",
          body: blobPart,
        });
        if (!resp.ok)
          throw new Error(
            `${t("smartSurveyor.uploading.error.partFailed")} ${partNumber} - ${ctx.file.name}`,
          );

        const eTag = resp.headers.get("ETag") || resp.headers.get("etag");
        ctx.parts.push({
          PartNumber: partNumber,
          ETag: eTag ? eTag.replaceAll('"', "") : null,
        });
        ctx.nextPartNumber = partNumber + 1;

        setUploadProgress((prev) => ({
          ...prev,
          [ctx.key]: Math.floor((partNumber / ctx.partCount) * 100),
        }));
      }

      // Complete single file
      await api.completeQuantitySurveyorMultipart(projectId, [
        {
          type: ctx.category,
          key: ctx.key,
          uploadId: ctx.uploadId,
          parts: ctx.parts,
        },
      ]);

      setUploadProgress((prev) => ({ ...prev, [ctx.key]: 100 }));
      setUploadStatus("success");
      setUploadStage(t("smartSurveyor.uploading.stage.completed"));
    } catch (error: any) {
      console.error("Resume upload error:", error);
      toast.error(
        error.message || t("smartSurveyor.uploading.error.resumeFailed"),
      );
      setUploadStatus("error");
      setUploadStage(t("smartSurveyor.uploading.error.resumeFailed"));
      bugsnagClient.notify(error);
    }
  };

  return (
    <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center z-[60]">
      <div className="bg-white rounded-lg p-6 max-w-3xl w-full mx-4">
        {/* Header */}
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-medium">
            {t("smartSurveyor.uploadTitle")}
          </h3>
          <button
            onClick={onClose}
            disabled={uploading}
            className="text-gray-400 hover:text-gray-500"
          >
            <X className="h-6 w-6" />
          </button>
        </div>
        {uploadStage && (
          <p className="text-sm text-gray-500 mb-2">{uploadStage}</p>
        )}
        <div className="space-y-4 max-h-[60vh] overflow-y-auto">
          {filesArray.map(({ file, category }) => {
            const ctx = Object.values(mpCtxMap).find((c) => c.file === file);
            const progress = ctx ? (uploadProgress[ctx.key] ?? 0) : 0;
            const canRemove = onRemoveFile && uploadStatus === "idle";

            return (
              <div
                key={`${category}-${file.name}-${file.lastModified}`}
                className="flex items-center justify-between bg-gray-50 p-3 rounded-lg border"
              >
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium truncate">{file.name}</p>
                  <p className="text-xs text-gray-500">
                    {file.size < 1024 * 1024
                      ? `${(file.size / 1024).toFixed(1)} KB`
                      : `${(file.size / (1024 * 1024)).toFixed(1)} MB`}
                  </p>
                </div>
                <div className="flex items-center space-x-2 flex-shrink-0">
                  {(uploadStatus === "uploading" ||
                    uploadStatus === "processing") && (
                    <div className="w-24 bg-gray-200 rounded-full h-2 overflow-hidden">
                      <div
                        className="h-2 bg-blue-500 transition-all duration-300"
                        style={{ width: `${progress}%` }}
                      />
                    </div>
                  )}
                  {uploadStatus === "success" && (
                    <CheckCircle className="text-green-500" />
                  )}
                  {uploadStatus === "error" && ctx && (
                    <button
                      onClick={() => resumeUpload(ctx)}
                      className="text-xs text-blue-600 underline"
                    >
                      {t("video_upload.resume")}
                    </button>
                  )}
                  {canRemove && (
                    <button
                      type="button"
                      onClick={() => onRemoveFile(category, file)}
                      className="p-1 text-gray-400 hover:text-red-500 transition-colors"
                      aria-label={`Remove ${file.name}`}
                    >
                      <X size={16} />
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
        {/* {uploadStatus === "success" && uploadResult && (
          <div className="mt-4 p-2 bg-green-50 rounded text-sm text-green-800">
            {t("smartSurveyor.uploading.completeSummary", {
              files: uploadResult.map((f: any) => f.key).join(", "),
            })}
          </div>
        )} */}
        <div className="flex justify-end space-x-3 mt-4">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm text-gray-700 hover:text-gray-900"
            disabled={uploading}
          >
            {t("common.cancel")}
          </button>
          <button
            onClick={startUpload}
            disabled={!filesArray.length || uploading}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-blue-400"
          >
            {uploading
              ? t("smartSurveyor.uploading.stage.uploading")
              : t("smartSurveyor.analysisWorkflow.analyzeButton")}
          </button>
        </div>
      </div>
    </div>
  );
};
